<template>
    <b-modal
            :visible="show"
            @hidden="hideEvents"
            @show="setFilters"
            :title="modelTitle"
            :id="modalId"
            class="modal-body-sm"
            body-class="pt-0"
    >
        <vue-element-loading :active="loading" :is-full-screen="false"/>
        <b-alert class="warning-message"
                 variant="warning"
                 :show="true"
        >
            {{ this.getWarningMessage() }}
        </b-alert>

        <b-row
            no-gutters
            v-if="jobRequestTypeId === jobRequestTypes.new || jobRequestTypeId === jobRequestTypes.add"
        >
          <b-col cols="12" class="pt-3">
            <legend class="col-form-label pt-0 col-form-label-sm">Assessment Type</legend>
            <multiselect
                v-model="filters.assessmentTypes.value"
                :multiple="filters.assessmentTypes.multiple"
                :searchable="false"
                label="assessmentType"
                :options="filters.assessmentTypes.options"
                :placeholder="filters.assessmentTypes.placeholder"
                :hide-selected="true"
                :value="filters.assessmentTypes.value"
                selectLabel=""
            >
              <template slot="caret">
                <caret-icon></caret-icon>
              </template>
            </multiselect>
          </b-col>
        </b-row>

        <b-row
                no-gutters
                v-if="jobRequestTypeId === jobRequestTypes.add"
        >
            <b-col cols="12" class="pt-3">
                <legend class="col-form-label pt-0 col-form-label-sm">Job</legend>
                <multiselect
                        v-model="filters.jobs.value"
                        :multiple="filters.jobs.multiple"
                        :searchable="false"
                        :options="jobOptions"
                        :placeholder="filters.jobs.placeholder"
                        :hide-selected="true"
                        :value="filters.jobs.value"
                        selectLabel=""
                        :disabled="disableJobSelect"
                >
                    <template slot="caret">
                        <caret-icon></caret-icon>
                    </template>
                </multiselect>
            </b-col>
        </b-row>

        <b-row
                no-gutters
                v-if="jobRequestTypeId === jobRequestTypes.remove"
        >
            <b-col cols="12" class="pt-3">
                <legend class="col-form-label pt-0 col-form-label-sm">Reason for removal</legend>
                <b-form-input v-model="form.removalReason"></b-form-input>
            </b-col>
        </b-row>

        <b-row
                no-gutters
                v-if="jobRequestTypeId === jobRequestTypes.cancel"
        >
            <b-col cols="12" class="pt-3">
                <legend class="col-form-label pt-0 col-form-label-sm">Reason for cancellation</legend>
                <b-form-input v-model="form.removalReason"></b-form-input>
            </b-col>
        </b-row>

        <template slot="modal-footer">
            <b-button
                    variant="outline-secondary"
                    @click="hideModal">
                Cancel
                <font-awesome-icon
                        :icon="['far', 'times']">
                </font-awesome-icon>
            </b-button>
            <b-button
                    :disabled="loading"
                    class="btn-arrow"
                    variant="primary"
                    @click="jobRequestTypeId === jobRequestTypes.cancel
                    ? cancelJobRequest() : createJobRequest()"
            >
                Submit
                <font-awesome-icon
                        :icon="['far', 'arrow-right']">
                </font-awesome-icon>
            </b-button>
        </template>
    </b-modal>
</template>

<script>
import CaretIcon from '@/components/Form/CaretIcon';
import ModalMixin from '@/components/Modal/ModalMixin';
import ConfirmationWarning from '@/components/Notifications/ConfirmationWarning';
import JobRequest from '@/services/Job/JobRequest';
import AssessmentType from '@/services/Assessment/AssessmentType';
import Job from '@/services/Job/Job';
import FiltersOptionsMixin from '@/components/Grid/FiltersOptionsMixin';
import dayjs from 'dayjs';
import { mapGetters } from 'vuex';

const FILTER_SET = 'SET';
const FILTER_DATE = 'DATE';

export default {
  name: 'Job-Request-Modal',
  components: {
    CaretIcon,
    ConfirmationWarning,
  },
  props: {
    complianceTypeId: {
      default: '',
    },
    assessmentTypeId: {
      default: '',
    },
    jobRequestTypeId: {
      default: '',
      required: true,
    },
    jobId: {
      default: '',
    },
    propertyId: {
      default: '',
    },
    jobRequestId: {
      default: '',
    },
    hasCatB: {
      type: Boolean,
      default: false,
    },
  },
  mixins: [
    ModalMixin,
    FiltersOptionsMixin,
  ],
  data() {
    return {
      modalId: 'jobRequestModal',
      loading: false,
      displayedNotification: false,
      unlocked: false,
      maxNumberOfSelectedActions: 300,
      selectedAssessmentType: null,
      jobRequestTypes: {
        new: JobRequest.NEW_JOB_REQUEST_ID(),
        add: JobRequest.ADDITION_TO_JOB_ID(),
        remove: JobRequest.REMOVAL_FROM_JOB_ID(),
        cancel: JobRequest.CANCEL_JOB_REQUEST(),
      },
      form: {
        jobRequestName: '',
        removalReason: '',
      },
      filters: {
        assessmentTypes: {
          type: FILTER_SET,
          placeholder: '',
          field: 'assessmentTypes',
          multiple: false,
          options: [],
          optionsFn: async () => {
            if (this.jobRequestTypeId === this.jobRequestTypes.cancel) {
              return {};
            }

            const { data } = await this.getAssessmentTypes();
            return data.data;
          },
          value: [],
          fill: true,
        },

        jobs: {
          type: FILTER_SET,
          placeholder: '',
          field: 'jobs',
          multiple: false,
          options: [],
          value: [],
          fill: true,
        },
      },
    };
  },

  computed: {
    ...mapGetters(['userFullName']),
    modelTitle() {
      let title = 'Request new job';

      if (this.jobRequestTypeId === this.jobRequestTypes.add) {
        title = 'Request addition to job';
      }

      if (this.jobRequestTypeId === this.jobRequestTypes.remove) {
        title = 'Request removal from job';
      }

      if (this.jobRequestTypeId === this.jobRequestTypes.cancel) {
        title = 'Cancel job request';
      }

      return title;
    },
    assessmentTypes() {
      return this.filters.assessmentTypes.options;
    },
    assessmentTypeOptions() {
      return this.filters.assessmentTypes.options.map(row => row.assessmentType);
    },
    jobs() {
      return this.filters.jobs.options;
    },
    jobOptions() {
      return this.filters.jobs.options.map(row => row.jobId + ' - ' + row.job);
    },
    disableJobSelect() {
      return (!this.selectedAssessmentType);
    },
  },

  watch: {
    'filters.assessmentTypes.value': {
      async handler(newSelected) {
        if (this.jobRequestTypeId === this.jobRequestTypes.add) {
          const { assessmentTypeId } = newSelected;
          const { data } = await this.getJobs(assessmentTypeId);
          this.filters.jobs.options = data.data;
        }
        this.selectedAssessmentType = newSelected;
      },
    },
  },

  methods: {
    getWarningMessage() {
      let message = '';
      const plural = this.nodeIds.length > 1;

      if (this.jobRequestTypeId === this.jobRequestTypes.cancel) {
        message = 'Are you sure you want to cancel this request?';
      }

      if (this.jobRequestTypeId === this.jobRequestTypes.new) {
        message = 'Are you sure you would like to request a job for ';
        message += this.nodeIds.length;
        message += plural ? ' properties?' : ' property?';

        if (this.hasCatB) {
          message = 'You have selected a Category B property, are you sure ';
          message += 'you would like to request a job for this property?';

          if (plural) {
            message = 'At least one of the selected properties is a Category B ';
            message += 'property, are you sure you would like to request a job ';
            message += `for ${this.nodeIds.length} properties?`;
          }
        }
      }

      if (this.jobRequestTypeId === this.jobRequestTypes.add) {
        message = 'Are you sure you would like to add ';
        message += this.nodeIds.length;
        message += plural ? ' properties' : ' property';
        message += ' to this job?';

        if (this.hasCatB) {
          message = 'You have selected a Category B property, are you sure ';
          message += 'you would like to add this property?';

          if (plural) {
            message = 'At least one of the selected properties is a Category B ';
            message += 'property, are you sure you would like to add ';
            message += `${this.nodeIds.length} properties?`;
          }
        }
      }

      if (this.jobRequestTypeId === this.jobRequestTypes.remove) {
        message = 'Are you sure you would like to remove this property from ';
        message += 'it\'s job?';
      }

      return message;
    },
    setFilters() {
      this.runOptionsFn();
    },
    async getAssessmentTypes() {
      return AssessmentType.list({ parentTypeId: this.complianceTypeId });
    },
    async getJobs(assessmentTypeId) {
      return Job.listByAssessmentType(assessmentTypeId);
    },
    async cancelJobRequest() {
      this.loading = true;
      const { removalReason } = this.form;

      if (!this.jobRequestId) {
        this.$notify({
          title: 'No Job request',
          text: 'Please make sure you are in the correct job request',
          type: 'error',
        });

        this.loading = false;
        return;
      }

      try {
        const { data } = await JobRequest.cancel(
          this.jobRequestId,
          removalReason,
        );
      } catch (e) {
// TODO: Use Error Mixin
        const { data } = e.response;
        if (data.error) {
          data.error.errors.name.forEach((error) => {
            this.error(error);
          });
        }
        if (data.message) {
          this.$notify({
            title: 'An error occurred',
            text: data.message,
            type: 'error',
          });
        }
        return;
      } finally {
        this.loading = false;
      }

      this.complete();

      this.$notify({
        duration: 7000,
        title: 'Job request cancelled',
        text: 'Job request has been cancelled.',
        type: 'success',
      });

      this.$emit('submitted');
    },
    async createJobRequest() {
      this.loading = true;
      const jobRequestName = this.generateJobRequestName();
      const { removalReason } = this.form;

      let jobId = 0;
      let assessmentTypeId = 0;

      if (this.jobRequestTypeId === this.jobRequestTypes.new) {
        if (!this.selectedAssessmentType) {
          this.$notify({
            title: 'No Assessment type selected',
            text: 'Please select an assessment type before requesting a new job',
            type: 'error',
          });

          this.loading = false;
          return;
        }

        let { assessmentTypeId } = this.selectedAssessmentType;
      }

      if (this.jobRequestTypeId === this.jobRequestTypes.add) {
        const selectedJob = this.filters.jobs.value;

        if (selectedJob.length === 0) {
          this.$notify({
            title: 'No Job selected',
            text: 'Please select a job before requesting a new job',
            type: 'error',
          });

          this.loading = false;
          return;
        }

        jobId = this.jobs.find(job => job.jobId + ' - ' + job.job === selectedJob).jobId;
        assessmentTypeId = this.jobs.find(job => job.jobId + ' - ' + job.job === selectedJob).assessmentTypeId;
      }

      if (this.jobRequestTypeId === this.jobRequestTypes.remove) {
        jobId = this.jobId;
        if (removalReason.length === 0) {
          this.$notify({
            title: 'No removal reason entered',
            text: 'Please give a reason for removal',
            type: 'error',
          });
          this.loading = false;
          return;
        }
      }

      if (jobRequestName.length === 0) {
        this.$notify({
          title: 'No Request name',
          text: 'Please give this request a name',
          type: 'error',
        });
        this.loading = false;
        return;
      }

      try {
        const { data } = await JobRequest.add(
          jobRequestName,
          this.complianceTypeId,
          assessmentTypeId,
          this.nodeIds,
          this.jobRequestTypeId,
          jobId,
          removalReason,
        );
      } catch (e) {
        const { data } = e.response;

        if (data.error) {
          data.error.errors.name.forEach((error) => {
            this.error(error);
          });
        }
        if (data.message) {
          this.$notify({
            title: 'An error occurred',
            text: data.message,
            type: 'error',
          });
        }
        return;
      } finally {
        this.loading = false;
      }

      this.complete();
      this.success();
    },

    generateJobRequestName() {
      const newDate = dayjs();
      const formattedDate = newDate.format('YYYY-MM-DD HH:mm:ss');
      const jobRequestName = formattedDate + ' - ' + this.userFullName;

      return jobRequestName;
    },

    success() {
      this.$notify({
        duration: 7000,
        title: 'Job request successful',
        text: 'The job has been requested.',
        type: 'success',
      });
      this.$emit('submitted');
    },

    error(message = 'Something went wrong. Please try again later.') {
      this.$notify({
        title: 'Error',
        text: message,
        type: 'error',
      });
    },

    complete() {
      this.hideModal();
      this.loading = false;
    },
  },
};
</script>

<style lang="scss" scoped>
    button.btn.btn-arrow.btn-primary {
        min-width: 115px;
    }
    .warning-message {
        margin-top: 15px;
    }
</style>
